import React, {
  useCallback, useMemo, useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Heading, Spinner, Grid, Alert, Link,
} from '@printdeal/ui-library';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import { productClient } from '@printdeal/product-selector';
import { variableAttributesArrayToObject } from '@printdeal/transformers';
import { navigate } from 'gatsby';
import { getShippingAddressesFromLineItem, useGetOrderQuery } from '@printdeal/checkout-frontend';
import { ErrorManagement } from '@printdeal/error-management/react';
import {
  Status,
} from '../../../../services/customer/CustomerProposalService';
import Head from '../../../PrivateHead';
import { messages } from '../../translations/proposals';
import { ServiceFactory } from '../../../../services/ServiceFactory';
import ProposalIteration from './ProposalIteration';
// @ts-expect-error
import { selectors as customerSelectors } from '../../../../store/slices/customerSlice';
import { RootState } from '../../../../store';
import { ProposalDetailBreadCrumbs } from './ProposalDetailBreadCrumbs';
import useSystemPages from '../../../../hooks/useSystemPages';

interface ViewProposalDetailPageProps {
  proposalId: string;
}

const proposalService = ServiceFactory.createCustomerProposalService();

const ViewProposalDetailPage = ({
  proposalId,
}: ViewProposalDetailPageProps) => {
  const intl = useIntl();
  const customerId = useSelector<RootState, string>((state) =>
    customerSelectors.getAccountId(state));

  const { data: proposalDetails, isLoading: isProposalLoading, refetch } = useQuery({
    queryKey: ['proposal-detail', customerId, proposalId],
    queryFn: () => proposalService.getProposalDetails(
      customerId,
      proposalId,
    ),
  });

  const {
    lineItemId, lineItemNumber, orderId, iterations,
  } = proposalDetails || {};

  const [accountDashboard] = useSystemPages('accountDashboard');
  const [showAlert, setShowAlert] = useState(false);
  const [customerActionStatus, setCustomerActionStatus] = useState< Status >();
  const { data, isLoading: isOrderLoading } = useGetOrderQuery({ orderId: orderId! });
  const orderDetails = data?.message;

  const lineItem = useMemo(() => {
    if (orderDetails && lineItemId) {
      return orderDetails.lineItems.find((item) => item.id === lineItemId);
    }
    return null;
  }, [orderDetails, lineItemId]);

  const getPromisedArrivalDate = async (): Promise<string | undefined> => {
    if (lineItem?.custom?.fields) {
      const {
        productKey, variableAttributes, cutOffTime,
      } = lineItem.custom.fields;

      const parsedAddress = getShippingAddressesFromLineItem(lineItem) || [];
      const shippingAddress = { CountryCode: parsedAddress[0].country, PostalCode: parsedAddress[0].postalCode };
      const formattedAttributes = variableAttributesArrayToObject(variableAttributes!);

      return productClient.getExpectedDeliveryDate({
        productKey: productKey!,
        variableAttributes: formattedAttributes,
        shippingAddress,
        quantity: lineItem.quantity,
        group: [],
        cutoffTime: cutOffTime,
      });
    }
    return undefined;
  };

  const handleNewFileDeliverConfirmation = () => {
    if (customerActionStatus === 'rejected') {
      navigate(`${accountDashboard.path}/orders/${orderId}?lineItemNumber=${lineItemNumber}`);
    }
  };

  const { data: deliveryDate = undefined } = useQuery({
    queryKey: ['customer-proposal-fetch-pad', lineItem?.id],
    queryFn: getPromisedArrivalDate,
    enabled: !!lineItem?.id,
  });

  const { isPending, mutate } = useMutation({
    mutationKey: ['update-customer-proposal-action', orderId, lineItemId],
    mutationFn: ({ customerAction, comments }: { customerAction: Status, comments?: string }) => {
      if (orderId && lineItemId) {
        const requestBody = comments ? { comments } : {};
        return proposalService.updateCustomerAction(
          { orderId, lineItemId, customerAction }, requestBody,
        );
      }
      return Promise.resolve();
    },
    onSuccess: () => {
      setShowAlert(true);
      refetch();
      handleNewFileDeliverConfirmation();
    },
    onError: (error) => {
      ErrorManagement.exception({
        exception: error,
        tags: {
          fileLocation: 'ProposalDetail',
        },
        fingerprint: ['customer-proposal-action'],
      });
    },
  });

  const handleProposalReview = useCallback(async (customerAction: Status, comments?: string) => {
    try {
      setCustomerActionStatus(customerAction);
      await mutate({ customerAction, comments });
      await refetch();
    } catch (error) {
      ErrorManagement.exception({
        exception: error,
        tags: {
          fileLocation: 'ProposalDetail',
        },
        fingerprint: ['customer-proposal-action'],
      });
    }
  }, [orderId, lineItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = isProposalLoading || isOrderLoading;

  const { defaultMessage = '', id = '' } = customerActionStatus ? messages[`${customerActionStatus}AlertInfo`] : {};
  const alertVariant = customerActionStatus === 'approved' ? 'success' : 'info';
  return (
    <Grid className="pd-gap-6">
      <Head
        title={`${intl.formatMessage(messages.proposalDetailTitle)} ${
          lineItemNumber || ''
        }`}
      />
      {
        isLoading ? (
          <div className="pd-flex pd-min-h-96 pd-flex-row pd-items-center pd-justify-center">
            <Spinner />
          </div>
        ) : null
      }
      {
        (proposalDetails && lineItem) ? (
          <>
            <ProposalDetailBreadCrumbs lineItemNumber={lineItemNumber} />
            {showAlert && (
            <Alert variant={alertVariant}>
              <FormattedMessage
                id={id}
                defaultMessage={defaultMessage}
                values={{
                  a: (chunks: React.ReactNode) => (
                    <Link href={`${accountDashboard?.path}/orders/${orderId}`}>
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </Alert>
            )}
            <Heading as="h1">
              <FormattedMessage
                id={messages.proposalDetailTitle.id}
                defaultMessage={messages.proposalDetailTitle.defaultMessage}
              />{' '}
              <span data-cy="orderNumberInTitle">{lineItemNumber}</span>
            </Heading>
            <hr />
            <p>
              <FormattedMessage
                id={messages.proposalNotesDescription.id}
                defaultMessage={messages.proposalNotesDescription.defaultMessage}
                values={{ br: <br /> }}
              />
            </p>
            <hr />
            {iterations?.map((iteration, index) => (
              <>
                <ProposalIteration
                  // @ts-expect-error type mismatch
                  lineItem={lineItem!}
                  key={iteration.id}
                  iteration={iteration}
                  version={iterations.length - index}
                  isWaiting={isPending}
                  handleProposalReview={handleProposalReview}
                  promisedArrivalDate={deliveryDate}
                  cutoffTime={lineItem.custom?.fields.cutOffTime}
                />
                { (index < iterations.length - 1) && <hr /> }
              </>
            ))}
          </>
        ) : null
      }
    </Grid>
  );
};

export default ViewProposalDetailPage;
